import { useState } from 'react';
import { ToggleRight } from 'lucide-react';
import { useCardStyles } from './cardStyles';
import { humanState, useEntity, useEntityFriendlyName } from './useEntity';
import { useHAService } from '../../context/HAConnectionContext';

export interface SwitchCardProps {
  entityId: string;
  title?: string;
}

export function SwitchCard({ entityId, title }: SwitchCardProps): JSX.Element {
  const styles = useCardStyles();
  const entity = useEntity(entityId);
  const name = useEntityFriendlyName(entityId, title);
  const { callService } = useHAService();
  const s = humanState(entity);
  const isOn = entity?.state === 'on';
  const [optimisticOn, setOptimisticOn] = useState<boolean | null>(null);
  const on = optimisticOn ?? isOn;

  const toggle = () => {
    const next = !on;
    setOptimisticOn(next);
    callService('switch', next ? 'turn_on' : 'turn_off', { entity_id: entityId }).catch((err) => {
      console.error('[SwitchCard] toggle failed', entityId, err);
      setOptimisticOn(null);
    });
  };

  return (
    <div
      className={styles.container}
      data-domain="switch"
      data-entity-id={entityId}
      data-state={on ? 'on' : 'off'}
    >
      <div className="flex items-center justify-between">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors ${
            on
              ? 'bg-emerald-500/20 text-emerald-500 dark:text-emerald-400'
              : styles.isDark
              ? 'bg-gray-700/60 text-gray-500'
              : 'bg-gray-100 text-gray-400'
          }`}
        >
          <ToggleRight className="h-5 w-5" />
        </div>
        <button
          type="button"
          onClick={toggle}
          aria-pressed={on}
          aria-label={name}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
            on ? 'bg-emerald-500' : styles.isDark ? 'bg-gray-600' : 'bg-gray-300'
          }`}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all"
            style={{ left: on ? 'calc(100% - 22px)' : '2px' }}
          />
        </button>
      </div>
      <div>
        <div className={`truncate text-sm font-medium ${styles.title}`}>{name}</div>
        <div
          className={`text-lg font-bold ${
            on ? 'text-emerald-500 dark:text-emerald-400' : styles.subtle
          }`}
        >
          {s.text}
        </div>
      </div>
    </div>
  );
}
